import type { ReviewStateArray, ReviewTypeArray } from "@/constants";
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  type QueryClient,
} from "@tanstack/react-query";
import z from "zod";
import { ReviewRequestVisibilityArray, ReviewTypeArray as ReviewTypeValues } from "@/constants";
import {
  batchRejectReviewRequests as batchRejectReviewRequestsApi,
  cancelReviewRequest as cancelReviewRequestApi,
  completeReviewRequest as completeReviewRequestApi,
  generateAiOptimizedDescriptionForReviewRequest as generateAiOptimizedDescriptionForReviewRequestApi,
  getAdminReviewRequest as getAdminReviewRequestApi,
  getAdminReviewRequests as getAdminReviewRequestsApi,
  getAdminReviewRequestsTable as getAdminReviewRequestsTableApi,
  getReviewRequest as getReviewRequestApi,
  getReviewRequestHistory as getReviewRequestHistoryApi,
  rejectReviewRequestsBeforeCreatedAt as rejectReviewRequestsBeforeCreatedAtApi,
  rejectReviewRequest as rejectReviewRequestApi,
  restoreReviewRequest as restoreReviewRequestApi,
  startReviewRequest as startReviewRequestApi,
  submitReviewRequest as submitReviewRequestApi,
  updateReviewRequest as updateReviewRequestApi,
} from "@/lib/api/generated/endpoints/review/review";
import type {
  AdminReviewRequestItem as ApiAdminReviewRequestItem,
  AdminReviewRequestsResponse as ApiAdminReviewRequestsResponse,
  AdminReviewRequestsTableResponse as ApiAdminReviewRequestsTableResponse,
  BatchRejectReviewRequestsInput,
  CompleteReviewRequestInput,
  RejectReviewRequestInput,
  RejectReviewRequestsBeforeInput,
  RejectReviewRequestsBeforeResponse,
  ReviewRequestResponse,
  SubmitReviewRequestResponse,
} from "@/lib/api/generated/model";

export const reviewRequestSchema = z.object({
  videoLinks: z
    .array(
      z.object({
        url: z
          .url("请输入有效的视频链接")
          .regex(
            /^https?:\/\/(?:[\w-]+\.)*(?:bilibili\.com|b23\.tv)\/.*/,
            "请输入有效的哔哩哔哩视频链接",
          ),
      }),
    )
    .min(1, "至少有一个视频链接地址"),
  description: z.string().min(10, "描述至少需要10个字符"),
  public: z.boolean().optional().default(false),
  type: z.enum(ReviewTypeValues).optional().default("review"),
});

export const reviewRequestIdSchema = z.object({
  id: z.uuid("请输入有效的复盘请求ID"),
});

export type ReviewRequestInput = z.input<typeof reviewRequestSchema>;

export type ReviewRequest = Omit<
  ReviewRequestResponse,
  | "aiOptimizedDescription"
  | "createdAt"
  | "queueEnteredAt"
  | "rejectedAt"
  | "reviewedAt"
  | "reviewerId"
  | "reviewingAt"
  | "status"
  | "type"
  | "updatedAt"
  | "videoLinks"
  | "visibility"
> & {
  aiOptimizedDescription: string | null;
  createdAt: Date;
  queueEnteredAt: Date;
  rejectedAt: Date | null;
  reviewedAt: Date | null;
  reviewerId: string | null;
  reviewingAt: Date | null;
  status: (typeof ReviewStateArray)[number];
  type: (typeof ReviewTypeArray)[number];
  updatedAt: Date;
  videoLinks: {
    url: string;
    bili?: {
      upUid: string;
    };
  }[];
  visibility: (typeof ReviewRequestVisibilityArray)[number];
};

export type ReviewRequestHistoryItem = ReviewRequest & {
  queuePosition: number | null;
};

export type ReviewRequestHistoryResponse = {
  requests: ReviewRequestHistoryItem[];
  cursor: string | null;
};

export type AdminReviewUser = Omit<ApiAdminReviewRequestItem["user"], "email"> & {
  email: string | null;
};

export type AdminReviewRequestItem = Omit<ApiAdminReviewRequestItem, "request" | "user"> & {
  request: ReviewRequest;
  user: AdminReviewUser;
};

export type AdminReviewRequestsResponse = Omit<
  ApiAdminReviewRequestsResponse,
  "cursor" | "requests"
> & {
  cursor: string | null;
  requests: AdminReviewRequestItem[];
};

export type AdminReviewRequestsTableResponse = Omit<
  ApiAdminReviewRequestsTableResponse,
  "requests"
> & {
  requests: AdminReviewRequestItem[];
};

type SubmitReviewRequestResult = Omit<SubmitReviewRequestResponse, "request"> & {
  request: ReviewRequest;
};

type ReviewMutationPayload = {
  data: ReviewRequestInput;
};

type ReviewUpdatePayload = {
  data: ReviewRequestInput & {
    id: string;
  };
};

type ReviewRequestIdPayload = {
  data: {
    id: string;
  };
};

type AdminCompleteReviewPayload = {
  data: CompleteReviewRequestInput & {
    id: string;
  };
};

type AdminRejectReviewPayload = {
  data: RejectReviewRequestInput & {
    id: string;
  };
};

type AdminBatchRejectReviewsPayload = {
  data: BatchRejectReviewRequestsInput;
};

type AdminRejectReviewsBeforeCreatedAtPayload = {
  data: RejectReviewRequestsBeforeInput;
};

export const reviewRequestQueryKeys = {
  user: ["review-request"] as const,
  userHistory: ["review-request", "history"] as const,
  adminList: ["admin", "review-requests"] as const,
  adminTable: ["admin", "review-requests-table"] as const,
};

export function invalidateReviewRequestQueries(
  queryClient: QueryClient,
  scope: "admin" | "user" | "all" = "all",
) {
  if (scope === "admin" || scope === "all") {
    queryClient.invalidateQueries({ queryKey: reviewRequestQueryKeys.adminList });
    queryClient.invalidateQueries({ queryKey: reviewRequestQueryKeys.adminTable });
  }
  if (scope === "user" || scope === "all") {
    queryClient.invalidateQueries({ queryKey: reviewRequestQueryKeys.user });
  }
}

function parseDate(value: string) {
  return new Date(value);
}

function parseNullableDate(value: null | string | undefined) {
  return value ? new Date(value) : null;
}

function adaptReviewRequest(request: ReviewRequestResponse): ReviewRequest {
  return {
    ...request,
    aiOptimizedDescription: request.aiOptimizedDescription ?? null,
    createdAt: parseDate(request.createdAt),
    queueEnteredAt: parseDate(request.queueEnteredAt),
    rejectedAt: parseNullableDate(request.rejectedAt),
    reviewedAt: parseNullableDate(request.reviewedAt),
    reviewerId: request.reviewerId ?? null,
    reviewingAt: parseNullableDate(request.reviewingAt),
    status: request.status as (typeof ReviewStateArray)[number],
    type: request.type as (typeof ReviewTypeArray)[number],
    updatedAt: parseDate(request.updatedAt),
    videoLinks: request.videoLinks.map((link) => ({
      url: link.url,
      bili: link.bili ?? undefined,
    })),
    visibility: request.visibility as (typeof ReviewRequestVisibilityArray)[number],
  };
}

function adaptSubmitReviewRequest(
  response: SubmitReviewRequestResponse,
): SubmitReviewRequestResult {
  return {
    ...response,
    request: adaptReviewRequest(response.request),
  };
}

function adaptAdminReviewRequestItem(item: ApiAdminReviewRequestItem): AdminReviewRequestItem {
  return {
    ...item,
    request: adaptReviewRequest(item.request),
    user: {
      ...item.user,
      email: item.user.email ?? null,
    },
  };
}

function adaptAdminReviewRequestsResponse(
  response: ApiAdminReviewRequestsResponse,
): AdminReviewRequestsResponse {
  return {
    ...response,
    cursor: response.cursor ?? null,
    requests: response.requests.map(adaptAdminReviewRequestItem),
  };
}

function adaptAdminReviewRequestsTableResponse(
  response: ApiAdminReviewRequestsTableResponse,
): AdminReviewRequestsTableResponse {
  return {
    ...response,
    requests: response.requests.map(adaptAdminReviewRequestItem),
  };
}

export async function submitReviewRequest({
  data,
}: ReviewMutationPayload): Promise<SubmitReviewRequestResult> {
  const input = reviewRequestSchema.parse(data);
  return adaptSubmitReviewRequest(await submitReviewRequestApi(input));
}

export async function updateReviewRequest({
  data,
}: ReviewUpdatePayload): Promise<SubmitReviewRequestResult> {
  const { id, ...requestInput } = data;
  reviewRequestIdSchema.parse({ id });
  const input = reviewRequestSchema.parse(requestInput);
  return adaptSubmitReviewRequest(await updateReviewRequestApi(id, input));
}

export async function getReviewRequest({ data }: ReviewRequestIdPayload): Promise<ReviewRequest> {
  reviewRequestIdSchema.parse(data);
  return adaptReviewRequest(await getReviewRequestApi(data.id));
}

export async function cancelReviewRequest({ data }: ReviewRequestIdPayload) {
  reviewRequestIdSchema.parse(data);
  return await cancelReviewRequestApi(data.id);
}

export async function restoreReviewRequest({ data }: ReviewRequestIdPayload) {
  reviewRequestIdSchema.parse(data);
  return await restoreReviewRequestApi(data.id);
}

export async function adminStartReview({ data }: ReviewRequestIdPayload) {
  reviewRequestIdSchema.parse(data);
  return await startReviewRequestApi(data.id);
}

export async function adminRejectReview({ data }: AdminRejectReviewPayload) {
  const { id, ...input } = data;
  reviewRequestIdSchema.parse({ id });
  return await rejectReviewRequestApi(id, input);
}

export async function adminCompleteReview({ data }: AdminCompleteReviewPayload) {
  const { id, ...input } = data;
  reviewRequestIdSchema.parse({ id });
  return await completeReviewRequestApi(id, input);
}

export async function generateAiOptimizedDescriptionForReviewRequest({
  data,
}: ReviewRequestIdPayload) {
  reviewRequestIdSchema.parse(data);
  return await generateAiOptimizedDescriptionForReviewRequestApi(data.id);
}

export async function adminBatchRejectReviews({ data }: AdminBatchRejectReviewsPayload) {
  return await batchRejectReviewRequestsApi(data);
}

export async function adminRejectReviewsBeforeCreatedAt({
  data,
}: AdminRejectReviewsBeforeCreatedAtPayload): Promise<RejectReviewRequestsBeforeResponse> {
  return await rejectReviewRequestsBeforeCreatedAtApi(data);
}

export function historyQueryOptions({ tab }: { tab?: (typeof ReviewStateArray)[number] }) {
  return infiniteQueryOptions({
    queryKey: [...reviewRequestQueryKeys.userHistory, tab],
    initialPageParam: null as null | string,
    queryFn: async ({ pageParam }): Promise<ReviewRequestHistoryResponse> => {
      const response = await getReviewRequestHistoryApi({
        cursor: pageParam ?? undefined,
        limit: 20,
        tab,
      });

      return {
        cursor: response.cursor ?? null,
        requests: response.requests.map((request) => ({
          ...adaptReviewRequest(request),
          queuePosition: request.queuePosition ?? null,
        })),
      };
    },
    placeholderData: keepPreviousData,
    getNextPageParam(page) {
      return page.cursor;
    },
  });
}

export function adminReviewRequestsInfiniteQueryOptions(
  filter: (typeof ReviewStateArray)[number],
  type: (typeof ReviewTypeArray)[number],
) {
  return infiniteQueryOptions({
    queryKey: [...reviewRequestQueryKeys.adminList, { filter, type }],
    initialPageParam: null as null | string,
    queryFn: async ({ pageParam }): Promise<AdminReviewRequestsResponse> => {
      return adaptAdminReviewRequestsResponse(
        await getAdminReviewRequestsApi({
          cursor: pageParam ?? undefined,
          filter,
          type,
        }),
      );
    },
    placeholderData: keepPreviousData,
    getNextPageParam(page) {
      return page.cursor;
    },
  });
}

export function adminReviewRequestQueryOptions(id: string) {
  return queryOptions({
    queryKey: [...reviewRequestQueryKeys.adminList, id],
    queryFn: async () => adaptAdminReviewRequestItem(await getAdminReviewRequestApi(id)),
  });
}

export function adminReviewRequestsTableQueryOptions(params: {
  page: number;
  pageSize: number;
  sortBy: "createdAt" | "queueEnteredAt" | "priorityQueue" | "status" | "type";
  sortOrder: "asc" | "desc";
  filter: (typeof ReviewStateArray)[number] | "all";
  type: (typeof ReviewTypeArray)[number] | "all";
}) {
  return queryOptions({
    queryKey: [...reviewRequestQueryKeys.adminTable, params],
    queryFn: async () =>
      adaptAdminReviewRequestsTableResponse(await getAdminReviewRequestsTableApi(params)),
    placeholderData: keepPreviousData,
  });
}
